import { useFormContext } from "react-hook-form";
import { ClipboardList, Clock, Tag, Ticket } from "lucide-react";
import { LGoButton, LGoDayPicker } from "@/ui/branded";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/ui/primitives/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { Input } from "@/ui/primitives/input";
import { TimePicker } from "@/ui/primitives/time-picker";
import { DateTimePicker } from "@/ui/primitives/datetime-picker";
import type { OfferFormValues } from "@/features/offers/form-schema";
import { templateSaleStartValue } from "@/features/offers/sale-start-prefill";
import { templateSaleEndValue } from "@/features/offers/sale-end-prefill";
import { saleCloseOf } from "@/domain/activity/rules";
import {
  capacityMessage,
  editableFields,
  unknownSales,
  type OfferSalesTally,
} from "@/domain/offer/rules";
import { endsNextDay, startOfLocalDay } from "@/domain/offer/series";
import { addDays, formatDate } from "@/lib/time";
import { timeAndPriceLockedMessage, unknownSalesMessage } from "@/features/offers/sales-locks";
import type { OfferActivityOption } from "@/data/repos/partnerOffersRepo";
import { OfferCustomFieldsSection } from "./OfferCustomFieldsSection";
import { FieldGroup } from "./FieldGroup";
import { InheritedValueNote } from "./InheritedValueNote";
import { offerAuthoringCopy } from "@/features/offers/offer-authoring-copy";

interface OfferFormFieldsProps {
  activities: OfferActivityOption[];
  sales: OfferSalesTally;
  reservedSeats: number;
  /** A published sleva keeps its activity and its places: the places are edited
   *  from its row in the list, and the activity is what it was published on. */
  isEditing: boolean;
}

export function OfferFormFields({
  activities,
  sales,
  reservedSeats,
  isEditing,
}: OfferFormFieldsProps) {
  const form = useFormContext<OfferFormValues>();
  const watchedActivityId = form.watch("activity_id");
  const selectedActivity = activities.find((a) => String(a.id) === watchedActivityId);
  const editable = editableFields(sales, reservedSeats);
  const [slotDay, slotStartTime, slotEndTime] = form.watch(["date", "start_time", "end_time"]);
  const endDayNote = endsNextDay(slotStartTime, slotEndTime)
    ? offerAuthoringCopy.slotEndsNextDay(formatDate(addDays(slotDay, 1), "dd. MM."))
    : null;
  // Editing a published sleva prefills nothing from the šablona, so nothing on
  // that screen is inherited and saying so would be a lie.
  const template = isEditing ? undefined : selectedActivity;
  const saleStartFromTemplate =
    template?.saleLeadTimeHours == null
      ? null
      : templateSaleStartValue(
          form.watch("date"),
          form.watch("start_time"),
          template.saleLeadTimeHours,
        );
  const templateSaleClose = template ? saleCloseOf(template) : null;
  const saleEndFromTemplate = templateSaleEndValue(
    slotDay,
    slotStartTime,
    slotEndTime,
    templateSaleClose,
  );

  return (
    <Form {...form}>
      <div className="space-y-5">
        <FormField
          control={form.control}
          name="activity_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{offerAuthoringCopy.templateLabel}</FormLabel>
              <Select onValueChange={field.onChange} value={field.value} disabled={isEditing}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={offerAuthoringCopy.templatePlaceholder} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {activities.map((activity) => (
                    <SelectItem key={activity.id} value={String(activity.id)}>
                      {activity.name}
                      {activity.priceOriginal && ` (${activity.priceOriginal} Kč)`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {!editable.time && (
          <p className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
            {sales === unknownSales ? unknownSalesMessage : timeAndPriceLockedMessage(sales.sold)}
          </p>
        )}

        <FieldGroup icon={<Clock className="h-3.5 w-3.5" />} title={offerAuthoringCopy.slotSection}>
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{offerAuthoringCopy.slotDateLabel}</FormLabel>
                <FormControl>
                  <LGoDayPicker
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!editable.time}
                    disabledDays={(day) => day < startOfLocalDay(new Date())}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-3">
            <FormField
              control={form.control}
              name="start_time"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{offerAuthoringCopy.slotStartLabel}</FormLabel>
                  <FormControl>
                    <TimePicker
                      ariaLabel={offerAuthoringCopy.slotStartLabel}
                      value={field.value}
                      onChange={field.onChange}
                      disabled={!editable.time}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="end_time"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{offerAuthoringCopy.slotEndLabel}</FormLabel>
                  <FormControl>
                    <TimePicker
                      ariaLabel={offerAuthoringCopy.slotEndLabel}
                      value={field.value}
                      onChange={field.onChange}
                      disabled={!editable.time}
                    />
                  </FormControl>
                  {endDayNote && <FormDescription>{endDayNote}</FormDescription>}
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FieldGroup>

        <FieldGroup icon={<Tag className="h-3.5 w-3.5" />} title={offerAuthoringCopy.priceSection}>
          <div className="grid grid-cols-2 gap-3">
            <FormField
              control={form.control}
              name="price_discounted"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{offerAuthoringCopy.priceLabel}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      disabled={!editable.price}
                      placeholder={
                        selectedActivity?.priceOriginal
                          ? `max ${selectedActivity.priceOriginal}`
                          : "Cena"
                      }
                      {...field}
                    />
                  </FormControl>
                  {selectedActivity?.priceOriginal != null && (
                    <FormDescription>
                      {offerAuthoringCopy.priceOriginal(selectedActivity.priceOriginal)}
                    </FormDescription>
                  )}
                  {template?.defaultPriceDiscounted != null && (
                    <InheritedValueNote
                      templateValueText={`${template.defaultPriceDiscounted} Kč`}
                      isOverridden={field.value !== String(template.defaultPriceDiscounted)}
                      onRestore={() =>
                        form.setValue("price_discounted", String(template.defaultPriceDiscounted))
                      }
                    />
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="max_slots"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{offerAuthoringCopy.capacityLabel}</FormLabel>
                  <FormControl>
                    <Input type="number" min={editable.capacityFloor} {...field} />
                  </FormControl>
                  {editable.capacityFloor > 1 && (
                    <FormDescription>
                      {capacityMessage.belowBooked(editable.capacityFloor)}
                    </FormDescription>
                  )}
                  {template?.defaultCapacity != null && (
                    <InheritedValueNote
                      templateValueText={String(template.defaultCapacity)}
                      isOverridden={field.value !== String(template.defaultCapacity)}
                      onRestore={() => form.setValue("max_slots", String(template.defaultCapacity))}
                    />
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </FieldGroup>

        <FieldGroup
          icon={<ClipboardList className="h-3.5 w-3.5" />}
          title={offerAuthoringCopy.customFieldsSection}
        >
          <OfferCustomFieldsSection
            inheritedFields={selectedActivity?.customFields}
            isEditing={isEditing}
          />
        </FieldGroup>

        <FieldGroup
          icon={<Ticket className="h-3.5 w-3.5" />}
          title={offerAuthoringCopy.saleSection}
        >
          <FormField
            control={form.control}
            name="sale_start"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{offerAuthoringCopy.saleStartLabel}</FormLabel>
                <FormControl>
                  <DateTimePicker
                    ref={field.ref}
                    ariaLabel={offerAuthoringCopy.saleStartLabel}
                    value={field.value || ""}
                    onChange={field.onChange}
                  />
                </FormControl>
                {!field.value && (
                  <FormDescription>{offerAuthoringCopy.saleStartBlank}</FormDescription>
                )}
                {saleStartFromTemplate !== null && template?.saleLeadTimeHours != null && (
                  <InheritedValueNote
                    templateValueText={offerAuthoringCopy.templateSaleLead(
                      template.saleLeadTimeHours,
                    )}
                    isOverridden={field.value !== saleStartFromTemplate}
                    onRestore={() => form.setValue("sale_start", saleStartFromTemplate)}
                  />
                )}
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="sale_end"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{offerAuthoringCopy.saleEndLabel}</FormLabel>
                <FormControl>
                  <DateTimePicker
                    ref={field.ref}
                    ariaLabel={offerAuthoringCopy.saleEndLabel}
                    value={field.value || ""}
                    onChange={field.onChange}
                  />
                </FormControl>
                {!field.value && (
                  <FormDescription>{offerAuthoringCopy.saleEndBlank}</FormDescription>
                )}
                {templateSaleClose !== null && saleEndFromTemplate !== "" && (
                  <InheritedValueNote
                    templateValueText={offerAuthoringCopy.templateSaleClose(templateSaleClose)}
                    isOverridden={field.value !== saleEndFromTemplate}
                    onRestore={() => form.setValue("sale_end", saleEndFromTemplate)}
                  />
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldGroup>
      </div>
    </Form>
  );
}

export { LGoButton };
